import { DateTime } from 'luxon'
import { UserDocument } from '../lib/userDocuments.slice'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { UserDocumentCategory } from '@/features/admin/user-document-categories/userDocumentCategories.slice'
import { TaxUserDocument } from '@/features/taxes/annual-taxes/taxUserDocuments.slice'

export type GroupedDocsEntry = {
  monthKey: string
  docs: UserDocument[]
}

export enum GroupByEnum {
  MonthWithinYear,
  CreatedAtYear,
  None,
  StatementYear,
  CategoryYear,
}

export const groupByType = (
  groupBy: GroupByEnum,
  documents: UserDocument[],
  taxUserDocuments?: TaxUserDocument[],
  documentCategories?: UserDocumentCategory[]
): Record<string, UserDocument[] | Record<string, UserDocument[]>> => {
  switch (groupBy) {
    // Bank Statements
    case GroupByEnum.StatementYear:
      return documents.reduce<Record<string, UserDocument[]>>((acc, doc) => {
        const rawMonth = doc.statementMonth ?? doc.statement?.statementMonth
        const year = rawMonth
          ? DateTime.fromFormat(
              rawMonth,
              DATE_FORMATS_LUXON.YEAR_MONTH
            ).year.toString()
          : new Date(doc.createdAt).getFullYear().toString()

        if (!acc[year]) acc[year] = []
        acc[year].push(doc)

        return acc
      }, {})

    // Bookkeeping
    case GroupByEnum.CreatedAtYear:
      return documents.reduce<Record<string, UserDocument[]>>((acc, doc) => {
        const year = new Date(doc.createdAt).getFullYear().toString()
        if (!acc[year]) acc[year] = []
        acc[year].push(doc)

        return acc
      }, {})

    // Taxes
    case GroupByEnum.CategoryYear:
      return documents.reduce<Record<string, UserDocument[]>>((acc, doc) => {
        // look up the category object
        const category =
          doc.userDocumentCategoryId !== null &&
          documentCategories?.[doc.userDocumentCategoryId]

        // try to pull a year out of the category’s internalName
        const categoryYear = category
          ? category.internalName?.match(/20\d\d/)?.[0]
          : null
        // find any matching taxUserDocument
        const taxUserDocument = taxUserDocuments?.find(
          (t) => t.userDocumentId === doc.id
        )

        // decide on the year
        let year: number
        if (taxUserDocument?.taxYear) {
          year = Number(taxUserDocument.taxYear)
        } else if (categoryYear) {
          year = Number(categoryYear)
        } else {
          year = new Date(doc.createdAt).getFullYear()
        }
        if (!acc[year]) acc[year] = []
        acc[year].push(doc)
        return acc
      }, {})

    // Receipts
    case GroupByEnum.MonthWithinYear:
      return documents.reduce<Record<string, Record<string, UserDocument[]>>>(
        (acc, doc) => {
          const date = new Date(doc.createdAt)
          const year = date.getFullYear().toString()
          const month = date.toLocaleString('default', { month: 'long' })
          const key = `${year}`
          if (!acc[key]) acc[key] = {}
          if (!acc[key][month]) acc[key][month] = []
          acc[key][month].push(doc)

          return acc
        },
        {}
      )

    // Incorporation | Other
    case GroupByEnum.None:
    default:
      return { All: documents }
  }
}
